import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";

const LS_KEY = "tcp:bookmarks";
const MIGRATED_KEY = "tcp:bookmarks_migrated_v1";
const TOKEN_KEY = "ctx_token";

// ---- localStorage helpers (fallback when anonymous) ----
function lsRead() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function lsWrite(items) {
    try {
        localStorage.setItem(LS_KEY, JSON.stringify(items));
        window.dispatchEvent(new CustomEvent("tcp:bookmarks-changed"));
    } catch {
        /* quota — quiet */
    }
}

function isAuthed() {
    try {
        return !!localStorage.getItem(TOKEN_KEY);
    } catch {
        return false;
    }
}

// ---- mapping: server doc ↔ frontend entry ----
// Server: { id, kind, ref_slug, ref_title, source_topic_cluster, added_at, last_opened_at, ... }
// Frontend entry shape (unchanged contract): { id, type, title, added_at, _server_id?, ... }
function serverToEntry(b) {
    return {
        id: b.ref_slug,
        type: b.kind,
        title: b.ref_title,
        added_at: b.added_at,
        last_opened_at: b.last_opened_at || null,
        cluster: b.source_topic_cluster || null,
        _server_id: b.id,
    };
}

function entryToServerPayload(entry) {
    return {
        kind: entry.type,
        ref_slug: String(entry.id),
        ref_title: String(entry.title || entry.id || "Untitled"),
        source_topic_cluster: entry.cluster || null,
    };
}

// One-shot migration: push any localStorage bookmarks to the server.
async function migrateLocalStorageIfNeeded() {
    if (!isAuthed()) return;
    if (localStorage.getItem(MIGRATED_KEY)) return;
    const local = lsRead();
    if (!local.length) {
        localStorage.setItem(MIGRATED_KEY, new Date().toISOString());
        return;
    }
    try {
        await api.post("/me/bookmarks/migrate", {
            items: local.map((e) => entryToServerPayload(e)),
        });
        localStorage.setItem(MIGRATED_KEY, new Date().toISOString());
        // We leave the localStorage payload in place as a fallback if the user
        // logs out, but a future cleanup pass could clear it once migration
        // proves itself in production.
    } catch {
        /* best-effort — will retry on next mount */
    }
}

/**
 * Bookmark store.
 *
 * - Authenticated → server-side (durable, cross-device, future-ready)
 * - Anonymous     → localStorage fallback (unchanged behaviour from v1)
 * - On first auth → one-shot migration from localStorage
 *
 * Exposed contract is identical to v1 so consumers (BookmarkButton, SavedPage,
 * ShareDock) require no changes:
 *   { items, isBookmarked(type, id), toggle(entry), remove(type, id) }
 */
export function useBookmarks() {
    const [items, setItems] = useState(lsRead);
    const authedRef = useRef(isAuthed());

    const refresh = useCallback(async () => {
        if (isAuthed()) {
            try {
                const res = await api.get("/me/bookmarks");
                const next = (res.data || []).map(serverToEntry);
                setItems(next);
                return;
            } catch {
                /* fall through to local */
            }
        }
        setItems(lsRead());
    }, []);

    useEffect(() => {
        // Initial load — migrate then fetch (server) or read (local).
        let cancelled = false;
        (async () => {
            if (isAuthed()) {
                await migrateLocalStorageIfNeeded();
            }
            if (!cancelled) await refresh();
        })();

        const onLocalChange = () => {
            if (!isAuthed()) setItems(lsRead());
        };
        const onStorage = (e) => {
            // Auth token changed in another tab → re-evaluate everything.
            if (e.key === TOKEN_KEY) {
                authedRef.current = isAuthed();
                refresh();
            } else if (e.key === LS_KEY && !isAuthed()) {
                setItems(lsRead());
            }
        };
        window.addEventListener("tcp:bookmarks-changed", onLocalChange);
        window.addEventListener("tcp:bookmarks-updated", onLocalChange);
        window.addEventListener("storage", onStorage);
        return () => {
            cancelled = true;
            window.removeEventListener("tcp:bookmarks-changed", onLocalChange);
            window.removeEventListener("tcp:bookmarks-updated", onLocalChange);
            window.removeEventListener("storage", onStorage);
        };
    }, [refresh]);

    const isBookmarked = useCallback(
        (type, id) => items.some((b) => b.type === type && b.id === id),
        [items]
    );

    const toggle = useCallback(
        async (entry) => {
            const exists = items.some(
                (b) => b.type === entry.type && b.id === entry.id
            );
            if (isAuthed()) {
                try {
                    if (exists) {
                        const found = items.find(
                            (b) => b.type === entry.type && b.id === entry.id
                        );
                        if (found?._server_id) {
                            await api.delete(`/me/bookmarks/${found._server_id}`);
                        }
                    } else {
                        await api.post("/me/bookmarks", entryToServerPayload(entry));
                    }
                    await refresh();
                    return !exists;
                } catch {
                    /* fall through to local cache so UI stays responsive */
                }
            }
            // Anonymous (or server failure) — localStorage path.
            const current = lsRead();
            const next = exists
                ? current.filter(
                    (b) => !(b.type === entry.type && b.id === entry.id)
                )
                : [
                    { ...entry, added_at: new Date().toISOString() },
                    ...current,
                ];
            lsWrite(next);
            setItems(next);
            return !exists;
        },
        [items, refresh]
    );

    const remove = useCallback(
        async (type, id) => {
            if (isAuthed()) {
                try {
                    const found = items.find(
                        (b) => b.type === type && b.id === id
                    );
                    if (found?._server_id) {
                        await api.delete(`/me/bookmarks/${found._server_id}`);
                    }
                    await refresh();
                    return;
                } catch {
                    /* fall through */
                }
            }
            const current = lsRead();
            const next = current.filter(
                (b) => !(b.type === type && b.id === id)
            );
            lsWrite(next);
            setItems(next);
        },
        [items, refresh]
    );

    return { items, isBookmarked, toggle, remove };
}
